import { auth } from "../firebase"

const BILLING_PORTAL_SESSION_URL = "/create_billing_portal_session"

const MAGIC_LINK_PORTAL_URL = "https://billing.stripe.com/p/login/3cI3cvbCm0uN7lZ8I5c3m00"

const OVERLAY_ID = "pinkcatai-billing-portal-overlay"

export const getMagicLinkPortalUrl = (email) => {
  if (email) {
    const params = new URLSearchParams({ prefilled_email: email })
    return `${MAGIC_LINK_PORTAL_URL}?${params.toString()}`
  }
  return MAGIC_LINK_PORTAL_URL
}

const showLoadingOverlay = () => {
  if (typeof document === "undefined") return null
  if (document.getElementById(OVERLAY_ID)) return document.getElementById(OVERLAY_ID)

  const overlay = document.createElement("div")
  overlay.id = OVERLAY_ID
  overlay.setAttribute("role", "status")
  overlay.setAttribute("aria-live", "polite")
  overlay.innerHTML = `
    <style>
      #${OVERLAY_ID} {
        position: fixed;
        inset: 0;
        background: rgba(255, 240, 247, 0.92);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2147483647;
        font-family: "Inter", Arial, sans-serif;
        animation: pinkcatai-fade-in 0.18s ease-out;
      }
      #${OVERLAY_ID} .pinkcatai-portal-card {
        background: white;
        border-radius: 16px;
        padding: 36px 44px;
        box-shadow: 0 12px 40px rgba(236, 72, 153, 0.18);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
        max-width: 360px;
        text-align: center;
      }
      #${OVERLAY_ID} .pinkcatai-portal-spinner {
        width: 48px;
        height: 48px;
        border: 4px solid #fce7f3;
        border-top-color: #ec4899;
        border-radius: 50%;
        animation: pinkcatai-spin 0.85s linear infinite;
      }
      #${OVERLAY_ID} .pinkcatai-portal-title {
        font-size: 1.05rem;
        font-weight: 700;
        color: #333;
        margin: 0;
      }
      #${OVERLAY_ID} .pinkcatai-portal-sub {
        font-size: 0.9rem;
        color: #666;
        margin: 0;
        line-height: 1.4;
      }
      @keyframes pinkcatai-spin {
        to { transform: rotate(360deg); }
      }
      @keyframes pinkcatai-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    </style>
    <div class="pinkcatai-portal-card">
      <div class="pinkcatai-portal-spinner" aria-hidden="true"></div>
      <p class="pinkcatai-portal-title">Opening your billing portal…</p>
      <p class="pinkcatai-portal-sub">You'll be redirected to Stripe in a moment.</p>
    </div>
  `
  document.body.appendChild(overlay)
  return overlay
}

const hideLoadingOverlay = () => {
  if (typeof document === "undefined") return
  const overlay = document.getElementById(OVERLAY_ID)
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay)
  }
}

/**
 * Opens the Stripe Customer Portal for the currently signed-in user.
 *
 * Primary path: backend creates a Billing Portal Session via the user's
 * stripe_customer_id (from Firestore) and returns a one-time URL. This works
 * regardless of whether the Stripe customer email matches the PinkCatAI login
 * email.
 *
 * Fallback: if no stripe_customer_id is linked or the backend call fails,
 * we redirect to the email-based magic-link login.
 */
export const openBillingPortal = async ({ user, onUnauthenticated, returnUrl } = {}) => {
  if (!auth.currentUser) {
    if (typeof onUnauthenticated === "function") {
      onUnauthenticated()
    }
    return
  }

  showLoadingOverlay()

  const fallbackToMagicLink = () => {
    window.location.href = getMagicLinkPortalUrl(user?.email)
  }

  try {
    const idToken = await auth.currentUser.getIdToken()
    const res = await fetch(BILLING_PORTAL_SESSION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        return_url: returnUrl || window.location.href,
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data?.url) {
      console.warn("Billing portal session unavailable, using magic-link fallback:", data)
      fallbackToMagicLink()
      return
    }
    window.location.href = data.url
  } catch (err) {
    console.error("Billing portal request failed, using magic-link fallback:", err)
    fallbackToMagicLink()
  }
  // Note: we deliberately don't hideLoadingOverlay() in success paths –
  // the page is about to unload via window.location.href, so leaving the
  // overlay up gives a smooth visual handoff to the Stripe portal load.
}

export { hideLoadingOverlay as _hideBillingPortalOverlay }
